import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { logout } from '../../store/authSlice';
import { UserRole } from '../../types';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  roles: UserRole[];
}

interface NavGroup {
  label: string;
  items: NavItem[];
  roles: UserRole[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'My Work',
    roles: ['superadmin', 'admin', 'user'],
    items: [
      {
        to: '/',
        label: 'My Forms',
        roles: ['superadmin', 'admin', 'user'],
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
        ),
      },
      {
        to: '/my/drafts',
        label: 'My Drafts',
        roles: ['superadmin', 'admin', 'user'],
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        ),
      },
      {
        to: '/my/published',
        label: 'My Published',
        roles: ['superadmin', 'admin', 'user'],
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
        ),
      },
    ],
  },
  {
    label: 'Content Pipeline',
    roles: ['superadmin', 'admin'],
    items: [
      {
        to: '/admin/sprint-actions',
        label: 'Sprint Actions',
        roles: ['superadmin', 'admin'],
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        ),
      },
      {
        to: '/admin/sprints',
        label: 'All Sprints',
        roles: ['superadmin', 'admin'],
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
        ),
      },
      {
        to: '/admin/forms',
        label: 'All Submissions',
        roles: ['superadmin', 'admin'],
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        ),
      },
      {
        to: '/admin/published-blogs',
        label: 'Published Blogs',
        roles: ['superadmin', 'admin'],
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
        ),
      },
      {
        to: '/admin/calendar',
        label: 'Content Calendar',
        roles: ['superadmin', 'admin'],
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        ),
      },
      {
        to: '/admin/backlinks-hub',
        label: 'Backlinks Hub',
        roles: ['superadmin', 'admin'],
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
        ),
      },
    ],
  },
  {
    label: 'Team',
    roles: ['superadmin', 'admin'],
    items: [
      {
        to: '/admin/user',
        label: 'Users',
        roles: ['superadmin', 'admin'],
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        ),
      },
      {
        to: '/admin/invites',
        label: 'Invitations',
        roles: ['superadmin', 'admin'],
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
        ),
      },
      {
        to: '/admin/admins',
        label: 'Admin Management',
        roles: ['superadmin'],
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>
        ),
      },
    ],
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const role = user?.role || 'user';

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const initials = user?.name
    ? user.name.split(' ').filter(Boolean).map(w => w[0].toUpperCase()).slice(0, 2).join('')
    : 'U';
  const roleLabel = role === 'superadmin' ? 'Super Admin' : role === 'admin' ? 'Admin' : 'User';
  const roleColor = role === 'superadmin' ? 'bg-purple-500/20 text-purple-200 border-purple-400/30'
    : role === 'admin' ? 'bg-trident-orange/25 text-trident-orange border-trident-orange/40'
    : 'bg-blue-500/20 text-blue-200 border-blue-400/30';

  const visibleGroups = NAV_GROUPS
    .filter(group => group.roles.includes(role))
    .map(group => ({
      ...group,
      items: group.items.filter(item => item.roles.includes(role)),
    }))
    .filter(group => group.items.length > 0);

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed lg:sticky top-0 left-0 h-screen w-72 z-50
          bg-gradient-to-b from-[#0F172A] via-[#111F38] to-[#0F172A]
          text-white flex flex-col
          transform transition-transform duration-300
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Decorative glow */}
        <div className="absolute top-10 -right-20 w-48 h-48 bg-trident-orange/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-24 -left-20 w-56 h-56 bg-trident-orange/8 rounded-full blur-3xl pointer-events-none" />

        {/* Logo */}
        <div className="relative px-5 py-5 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
              <img src="/trident-logo.png" alt="itTrident" className="w-7 h-7 object-contain" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-white text-base leading-tight truncate">
                Blog <span className="text-trident-orange">Automation</span>
              </div>
              <div className="text-[9px] uppercase tracking-[2px] text-white/40 font-semibold">
                AI Content Pipeline
              </div>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden ml-auto w-8 h-8 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition flex items-center justify-center"
              aria-label="Close menu"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="relative flex-1 overflow-y-auto px-3 py-4 space-y-6">
          {visibleGroups.map((group) => (
            <div key={group.label}>
              <div className="text-[10px] uppercase tracking-widest text-white/30 font-display font-bold px-3 mb-2">
                {group.label}
              </div>
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 px-3 py-2.5 rounded-xl font-display font-semibold text-sm transition-all duration-200 relative
                      ${isActive
                        ? 'bg-trident-gradient text-white shadow-trident'
                        : 'text-white/70 hover:text-white hover:bg-white/5'}`
                    }
                  >
                    <span className="flex-shrink-0">{item.icon}</span>
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Profile footer */}
        <div className="relative border-t border-white/5 p-3">
          <ProfileCard
            initials={initials}
            name={user?.name || 'Account'}
            email={user?.email || ''}
            roleLabel={roleLabel}
            roleColor={roleColor}
            onLogout={handleLogout}
          />
        </div>
      </aside>
    </>
  );
};

interface ProfileCardProps {
  initials: string;
  name: string;
  email: string;
  roleLabel: string;
  roleColor: string;
  onLogout: () => void;
}

const ProfileCard: React.FC<ProfileCardProps> = ({ initials, name, email, roleLabel, roleColor, onLogout }) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const goToProfile = () => {
    setMenuOpen(false);
    navigate('/profile');
  };

  return (
    <div ref={ref} className="relative">
      {menuOpen && (
        <div className="absolute bottom-full left-0 right-0 mb-2 bg-white text-trident-gray-900 rounded-xl shadow-card-hover border border-trident-gray-100 overflow-hidden animate-slide-up">
          <button
            onClick={goToProfile}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-trident-gray-700 hover:bg-trident-orange-light hover:text-trident-orange transition"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            Profile Settings
          </button>
          <div className="border-t border-trident-gray-100" />
          <button
            onClick={() => { onLogout(); setMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-red-600 hover:bg-red-50 transition"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Sign out
          </button>
        </div>
      )}
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition group"
      >
        <div className="w-10 h-10 rounded-xl bg-trident-gradient flex items-center justify-center text-white font-display font-bold text-sm shadow-trident flex-shrink-0">
          {initials}
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="font-display font-semibold text-sm text-white truncate">{name}</div>
          <div className="flex items-center gap-1.5">
            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${roleColor}`}>{roleLabel}</span>
            <span className="text-[10px] text-white/50 truncate">{email}</span>
          </div>
        </div>
        <svg className="text-white/40 group-hover:text-white/70 transition flex-shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
      </button>
    </div>
  );
};

export default Sidebar;
